import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package, MapPin, Heart, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { ordersApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { formatPrice } from '../../utils/currency';

const AccountDashboard = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('es') ? 'es-CO' : 'en-US';
  const { toast } = useToast();
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      // Get user info from token (you could decode JWT or fetch from /api/auth/me)
      const userEmail = localStorage.getItem('userEmail') || t('accountMore.userFallback');
      setUser({ email: userEmail });

      // Fetch recent orders
      const ordersData = await ordersApi.getOrders(1, 3, token);
      setRecentOrders(ordersData.orders || ordersData || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const quickLinks = [
    { icon: Package, label: t('dashboard.viewAllOrders'), link: '/account/orders', color: 'text-blue-600' },
    { icon: MapPin, label: t('dashboard.manageAddresses'), link: '/account/addresses', color: 'text-green-600' },
    { icon: Heart, label: t('dashboard.myWishlist'), link: '/account/wishlist', color: 'text-red-600' },
  ];

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#be1f1a]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-semibold text-[#2C2420] mb-2">
          {t('dashboard.welcomeBack')}{user?.email ? `, ${user.email.split('@')[0]}` : ''}!
        </h2>
        <p className="text-gray-600">
          {t('dashboard.manageText')}
        </p>
      </div>

      {/* Quick Links */}
      <div className="grid md:grid-cols-3 gap-4">
        {quickLinks.map((item, index) => {
          const Icon = item.icon;
          return (
            <Link key={index} to={item.link}>
              <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer">
                <Icon className={`h-8 w-8 ${item.color} mb-3`} />
                <h3 className="font-semibold text-gray-900">{item.label}</h3>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-[#2C2420]">{t('dashboard.recentOrders')}</h3>
          <Link to="/account/orders">
            <Button variant="ghost" size="sm" className="text-[#be1f1a] hover:text-[#a71b17]">
              {t('dashboard.viewAll')} <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>

        {recentOrders.length === 0 ? (
          <div className="text-center py-8">
            <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">{t('dashboard.noOrders')}</p>
            <Link to="/shop">
              <Button className="bg-[#be1f1a] hover:bg-[#a71b17]">
                {t('dashboard.startShopping')}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {recentOrders.map((order) => (
              <Link key={order.id || order._id} to={`/account/orders/${order.orderNumber}`}>
                <div className="border rounded-lg p-4 hover:border-[#be1f1a] transition-colors cursor-pointer">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-[#be1f1a]">#{order.orderNumber}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(order.createdAt).toLocaleDateString(dateLocale, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                      {t(`orders.${order.status}`, { defaultValue: order.status })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-600">
                      {order.items?.length || 0} {t('dashboard.items')}
                    </p>
                    <p className="font-semibold text-[#2C2420]">
                      {formatPrice(order.total, order.currency)}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountDashboard;